import React, { useState, useEffect, useCallback } from 'react';
import {
  Scale,
  Download,
  Trash2,
  Settings2,
  Sparkles,
  Zap,
  Crown,
  MessageSquare,
  Plus,
  Clock,
  ChevronRight,
  ChevronLeft,
  MoreVertical,
  Edit2,
  Archive,
  Loader2
} from 'lucide-react';
import { AIWorkspaceArea } from './AIWorkspaceArea';
import { useLegalAI } from '../../../../hooks/useLegalAI';
import {
  createContractContext,
  AI_MODELS,
  AIModel,
  ConversationSummary
} from '../../../../services/legalAIService';
import { Contract, ContractAnalysisJSON } from '../../../../types/contract.types';

interface EmbeddedAIWorkspaceProps {
  contract: Contract;
  jsonAnalysis?: ContractAnalysisJSON | null;
  analysisId?: string;
}

const getTierIcon = (tier: 'premium' | 'standard' | 'economy') => {
  switch (tier) {
    case 'premium':
      return <Crown className="w-3 h-3 text-amber-500" />;
    case 'standard':
      return <Sparkles className="w-3 h-3 text-blue-500" />;
    case 'economy':
      return <Zap className="w-3 h-3 text-green-500" />;
  }
};

const getTierColor = (tier: 'premium' | 'standard' | 'economy') => {
  switch (tier) {
    case 'premium':
      return 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800';
    case 'standard':
      return 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800';
    case 'economy':
      return 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800';
  }
};

const formatRelativeTime = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

export const EmbeddedAIWorkspace: React.FC<EmbeddedAIWorkspaceProps> = ({
  contract,
  jsonAnalysis,
  analysisId
}) => {
  const [activeClause, setActiveClause] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedModel, setSelectedModel] = useState<AIModel>('claude-sonnet-5-5');
  const [showConversationSidebar, setShowConversationSidebar] = useState(true);
  const [editingConversationId, setEditingConversationId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [contextMenuId, setContextMenuId] = useState<string | null>(null);

  // Create contract context from the contract data - now includes full analysis
  const contractContextData = {
    _id: contract._id || contract.id,
    contractTitle: contract.contractTitle || 'Untitled Contract',
    contractType: contract.category || 'General',
    supplier: contract.supplier || 'Unknown Supplier',
    startDate: contract.startDate,
    endDate: contract.endDate,
    value: contract.value
  };

  // Get provider from selected model
  const modelConfig = AI_MODELS.find(m => m.id === selectedModel);

  // Legal AI hook with persistence - pass full analysis for contract-aware responses
  const legalAI = useLegalAI({
    mode: 'standard',
    contractContext: createContractContext(contractContextData, jsonAnalysis)
  });

  // Load conversations when contract changes
  useEffect(() => {
    const contractId = contract._id || contract.id;
    if (contractId) {
      legalAI.loadConversations(contractId);
    }
  }, [contract._id, contract.id]);

  // Update provider when model changes
  useEffect(() => {
    if (modelConfig) {
      legalAI.setProvider(modelConfig.provider);
    }
  }, [selectedModel, modelConfig]);

  // Update contract context when contract or analysis changes
  useEffect(() => {
    legalAI.setContractContext(createContractContext(contractContextData, jsonAnalysis));
  }, [contract.id, contract._id, jsonAnalysis]);

  const handleClauseSelect = useCallback((clauseText: string) => {
    setActiveClause(clauseText);
  }, []);

  const handleExport = useCallback(() => {
    const messages = legalAI.conversationHistory
      .filter(m => m.role !== 'system')
      .map(m => `## ${m.role === 'user' ? 'You' : 'AI Assistant'}\n\n${m.content}`)
      .join('\n\n---\n\n');

    const contractInfo = `# Legal AI Conversation\n\n**Contract:** ${contract.contractTitle}\n**Supplier:** ${contract.supplier}\n**Model:** ${modelConfig?.name || selectedModel}\n**Date:** ${new Date().toLocaleDateString()}\n\n---\n\n`;

    const fullContent = contractInfo + messages;

    const blob = new Blob([fullContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `legal-ai-conversation-${contract.contractTitle?.replace(/[^a-z0-9]/gi, '_')}-${new Date().toISOString().split('T')[0]}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [legalAI.conversationHistory, contract, modelConfig, selectedModel]);

  const handleClearConversation = useCallback(() => {
    if (window.confirm('Are you sure you want to clear the conversation? This cannot be undone.')) {
      legalAI.clearConversation();
    }
  }, [legalAI]);

  const handleNewConversation = useCallback(async () => {
    const contractId = contract._id || contract.id;
    if (contractId) {
      await legalAI.startNewConversation(contractId, selectedModel, legalAI.mode);
    }
  }, [contract._id, contract.id, legalAI, selectedModel]);

  const handleSelectConversation = useCallback(async (conversationId: string) => {
    await legalAI.selectConversation(conversationId);
    setContextMenuId(null);
  }, [legalAI]);

  const handleRenameConversation = useCallback(async (conversationId: string) => {
    if (editingTitle.trim()) {
      await legalAI.renameConversation(conversationId, editingTitle.trim());
    }
    setEditingConversationId(null);
    setEditingTitle('');
  }, [legalAI, editingTitle]);

  const handleArchiveConversation = useCallback(async (conversationId: string) => {
    await legalAI.archiveConversation(conversationId);
    setContextMenuId(null);
  }, [legalAI]);

  const handleDeleteConversation = useCallback(async (conversationId: string) => {
    if (window.confirm('Are you sure you want to delete this conversation? This cannot be undone.')) {
      await legalAI.removeConversation(conversationId);
    }
    setContextMenuId(null);
  }, [legalAI]);

  // Group models by provider
  const claudeModels = AI_MODELS.filter(m => m.provider === 'anthropic');
  const openaiModels = AI_MODELS.filter(m => m.provider === 'openai');

  return (
    <div className="flex h-full min-h-[600px]">
      {/* Conversation Sidebar */}
      <div className={`${showConversationSidebar ? 'w-64' : 'w-0'} transition-all duration-300 overflow-hidden border-r border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-gray-800/50 flex flex-col`}>
        {showConversationSidebar && (
          <>
            {/* Sidebar Header */}
            <div className="p-3 border-b border-gray-200 dark:border-dark-border">
              <button
                onClick={handleNewConversation}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                New Conversation
              </button>
            </div>

            {/* Conversation List */}
            <div className="flex-1 overflow-y-auto">
              {legalAI.isLoadingConversations ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : legalAI.conversations.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No conversations yet</p>
                  <p className="text-xs mt-1">Start a new conversation to begin</p>
                </div>
              ) : (
                <div className="py-2">
                  {legalAI.conversations.map((convo) => (
                    <div
                      key={convo._id}
                      className={`relative group mx-2 mb-1 rounded-lg transition-colors ${
                        legalAI.activeConversationId === convo._id
                          ? 'bg-purple-100 dark:bg-purple-900/30'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      {editingConversationId === convo._id ? (
                        <div className="p-2">
                          <input
                            type="text"
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            onBlur={() => handleRenameConversation(convo._id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleRenameConversation(convo._id);
                              if (e.key === 'Escape') {
                                setEditingConversationId(null);
                                setEditingTitle('');
                              }
                            }}
                            className="w-full px-2 py-1 text-sm border border-purple-300 dark:border-purple-700 rounded bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500"
                            autoFocus
                          />
                        </div>
                      ) : (
                        <button
                          onClick={() => handleSelectConversation(convo._id)}
                          className="w-full text-left p-2 pr-8"
                        >
                          <div className="flex items-start gap-2">
                            <MessageSquare className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                              legalAI.activeConversationId === convo._id
                                ? 'text-purple-600 dark:text-purple-400'
                                : 'text-gray-400'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium truncate ${
                                legalAI.activeConversationId === convo._id
                                  ? 'text-purple-900 dark:text-purple-100'
                                  : 'text-gray-900 dark:text-white'
                              }`}>
                                {convo.title}
                              </p>
                              <div className="flex items-center gap-1 mt-0.5">
                                <Clock className="w-3 h-3 text-gray-400" />
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {formatRelativeTime(convo.updatedAt)}
                                </span>
                                <span className="text-xs text-gray-400 dark:text-gray-500">
                                  ({convo.messageCount} msgs)
                                </span>
                              </div>
                            </div>
                          </div>
                        </button>
                      )}

                      {/* Context Menu Button */}
                      {editingConversationId !== convo._id && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setContextMenuId(contextMenuId === convo._id ? null : convo._id);
                          }}
                          className="absolute right-2 top-2 p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-opacity"
                        >
                          <MoreVertical className="w-4 h-4 text-gray-500" />
                        </button>
                      )}

                      {/* Context Menu */}
                      {contextMenuId === convo._id && (
                        <div className="absolute right-0 top-8 z-10 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1">
                          <button
                            onClick={() => {
                              setEditingConversationId(convo._id);
                              setEditingTitle(convo.title);
                              setContextMenuId(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <Edit2 className="w-4 h-4" />
                            Rename
                          </button>
                          <button
                            onClick={() => handleArchiveConversation(convo._id)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <Archive className="w-4 h-4" />
                            Archive
                          </button>
                          <button
                            onClick={() => handleDeleteConversation(convo._id)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border-b border-gray-200 dark:border-dark-border">
          <div className="flex items-center gap-3">
            {/* Sidebar Toggle */}
            <button
              onClick={() => setShowConversationSidebar(!showConversationSidebar)}
              className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              title={showConversationSidebar ? 'Hide conversations' : 'Show conversations'}
            >
              {showConversationSidebar ? (
                <ChevronLeft className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>

            <div className="p-2 bg-purple-100 dark:bg-purple-900/50 rounded-lg">
              <Scale className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Legal AI Assistant</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                AI-powered contract analysis for {contract.contractTitle}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Settings Toggle */}
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`p-2 rounded-lg transition-colors ${
                showSettings
                  ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/50 dark:text-purple-400'
                  : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
              title="AI Settings"
            >
              <Settings2 className="w-4 h-4" />
            </button>

            {/* Export */}
            <button
              onClick={handleExport}
              disabled={legalAI.conversationHistory.length === 0}
              className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Export Conversation"
            >
              <Download className="w-4 h-4" />
            </button>

            {/* Clear */}
            <button
              onClick={handleClearConversation}
              disabled={legalAI.conversationHistory.length === 0}
              className="p-2 text-gray-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Clear Conversation"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Settings Panel (collapsible) */}
        {showSettings && (
          <div className="px-4 py-4 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-dark-border">
            <div className="space-y-4">
              {/* Mode Selector */}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 w-20">Mode:</label>
                <select
                  value={legalAI.mode}
                  onChange={(e) => legalAI.setMode(e.target.value as any)}
                  className="flex-1 text-sm px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500/20"
                >
                  <option value="standard">Standard Analysis</option>
                  <option value="legal-deep-dive">Legal Deep-Dive</option>
                  <option value="simplified">Simplified Explanation</option>
                  <option value="jurisdiction">Jurisdiction Focus</option>
                  <option value="negotiation">Negotiation Support</option>
                </select>
              </div>

              {/* Model Selection */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">AI Model:</label>
                <div className="grid grid-cols-2 gap-3">
                  {/* Claude Models */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Anthropic Claude</p>
                    {claudeModels.map((model) => (
                      <button
                        key={model.id}
                        onClick={() => setSelectedModel(model.id)}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          selectedModel === model.id
                            ? `${getTierColor(model.tier)} ring-2 ring-purple-500 dark:ring-purple-400`
                            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm text-gray-900 dark:text-white">{model.name}</span>
                          {getTierIcon(model.tier)}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{model.description}</p>
                      </button>
                    ))}
                  </div>

                  {/* OpenAI Models */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">OpenAI ChatGPT</p>
                    {openaiModels.map((model) => (
                      <button
                        key={model.id}
                        onClick={() => setSelectedModel(model.id)}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          selectedModel === model.id
                            ? `${getTierColor(model.tier)} ring-2 ring-purple-500 dark:ring-purple-400`
                            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm text-gray-900 dark:text-white">{model.name}</span>
                          {getTierIcon(model.tier)}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{model.description}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Selected Model Info */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Selected:</span>
                  <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                    {modelConfig?.name || selectedModel}
                  </span>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-medium">{jsonAnalysis?.clauses?.length || 0}</span> clauses loaded
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main AI Workspace */}
        <div className="flex-1 overflow-hidden">
          <AIWorkspaceArea
            legalAI={legalAI}
            activeClause={activeClause}
            onClauseSelect={handleClauseSelect}
            hasContract={true}
          />
        </div>

        {/* Legal Disclaimer Footer */}
        <div className="px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-t border-amber-200 dark:border-amber-800">
          <p className="text-xs text-amber-800 dark:text-amber-300 text-center">
            AI-generated suggestions require professional legal review before implementation.
            This tool provides guidance only and does not constitute legal advice.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmbeddedAIWorkspace;
